function normalizeText(value) {
  if (value === undefined || value === null) return '';
  return String(value).trim();
}

function buildSimpleMasterDefinition({
  key,
  label,
  valueField,
  sampleValue,
}) {
  return {
    key,
    label,
    endpoint: key,
    description: `${label}マスタを管理します。`,
    fields: [
      {
        key: valueField,
        label,
        type: 'text',
        required: true,
        width: '280px',
      },
    ],
    templateHeaders: [valueField],
    templateExample: {
      [valueField]: sampleValue,
    },
    fromApi(item) {
      return {
        id: item.id,
        [valueField]: item[valueField] || '',
      };
    },
    toApiPayload(row) {
      return {
        [valueField]: normalizeText(row[valueField]),
      };
    },
    hasMeaningfulValue(row) {
      return Boolean(normalizeText(row[valueField]));
    },
    getUniqueKey(row) {
      return normalizeText(row[valueField]).toLocaleLowerCase('ja');
    },
    validateRow(row) {
      const errors = [];
      if (!normalizeText(row[valueField])) {
        errors.push(`${label}は必須です。`);
      }
      return errors;
    },
  };
}

function buildSimpleUserDefinition({ key, label, valueField }) {
  return {
    key,
    label,
    endpoint: key,
    description: `${label}を管理します。`,
    fields: [
      { key: valueField, label, type: 'text', required: true, width: '280px' },
    ],
    fromApi(item) {
      return { id: item.id, [valueField]: item[valueField] || '' };
    },
    toApiPayload(row) {
      return { [valueField]: normalizeText(row[valueField]) };
    },
    hasMeaningfulValue(row) {
      return Boolean(normalizeText(row[valueField]));
    },
    getUniqueKey(row) {
      return normalizeText(row[valueField]).toLocaleLowerCase('ja');
    },
    validateRow(row) {
      const errors = [];
      if (!normalizeText(row[valueField])) errors.push(`${label}は必須です。`);
      return errors;
    },
  };
}

export const USER_DEFINITIONS = [
  {
    key: 'users',
    label: 'ユーザー',
    endpoint: 'users',
    description: 'ユーザーを管理します。',
    fields: [
      { key: 'employee_id', label: '社員番号', type: 'text', required: true, width: '180px', readOnlyOnEdit: true },
      { key: 'user_name', label: '名前', type: 'text', required: true, width: '180px' },
      { key: 'email', label: 'メール', type: 'text', required: true, width: '280px', readOnlyOnEdit: true },
    ],
    fromApi(item) {
      return {
        id: item.id,
        employee_id: item.employee_id || '',
        user_name: item.user_name || '',
        email: item.email || '',
      };
    },
    toApiPayload(row) {
      return {
        employee_id: normalizeText(row.employee_id),
        user_name: normalizeText(row.user_name),
        email: normalizeText(row.email),
      };
    },
    hasMeaningfulValue(row) {
      return Boolean(normalizeText(row.employee_id));
    },
    getUniqueKey(row) {
      return normalizeText(row.employee_id).toLocaleLowerCase('ja');
    },
    validateRow(row) {
      const errors = [];
      if (!normalizeText(row.employee_id)) errors.push('社員番号は必須です。');
      if (!normalizeText(row.user_name)) errors.push('名前は必須です。');
      if (!normalizeText(row.email)) errors.push('メールは必須です。');
      return errors;
    },
  },
  {
    key: 'permission_managements',
    label: '権限管理',
    endpoint: 'permission_managements',
    description: 'ユーザーと機能・権限の紐付けを管理します。',
    fields: [
      {
        key: 'employee_id',
        label: '社員番号',
        type: 'select',
        required: true,
        width: '180px',
        getOptions: ({ supportingData }) => [
          '',
          ...(supportingData.users || []).map((u) => u.employee_id),
        ],
        getOptionLabel: ({ value, supportingData }) => {
          if (!value) return '選択してください';
          const user = (supportingData.users || []).find((u) => u.employee_id === value);
          return user ? value + '（' + user.user_name + '）' : value;
        },
      },
      {
        key: 'user_name_display',
        label: '名前',
        type: 'text',
        width: '140px',
        displayOnly: true,
      },
      {
        key: 'function_name',
        label: '機能',
        type: 'select',
        required: true,
        width: '180px',
        readOnlyOnEdit: true,
        getOptions: ({ supportingData }) => {
          if (supportingData.defaultFunction) {
            return [supportingData.defaultFunction];
          }
          return ['', ...(supportingData.functions || []).map((f) => f.function)];
        },
        getDefaultValue: ({ supportingData }) => supportingData.defaultFunction || '',
      },
      {
        key: 'permission_name',
        label: '権限',
        type: 'select',
        required: true,
        width: '140px',
        getOptions: ({ supportingData }) => [
          '',
          ...(supportingData.permissions || []).map((p) => p.permission),
        ],
      },
    ],
    fromApi(item) {
      return {
        id: item.id,
        employee_id: item.employee_id || '',
        user_name_display: item.employee_name || '',
        function_name: item.function_name || '',
        permission_name: item.permission_name || '',
      };
    },
    toApiPayload(row, context) {
      const fnName = normalizeText(row.function_name);
      const functionId = context.functionByName.get(fnName.toLocaleLowerCase('ja'))
        || context.supportingData?.defaultFunctionId
        || null;
      const pnName = normalizeText(row.permission_name);
      const permissionId = context.permissionByName.get(pnName.toLocaleLowerCase('ja'))
        || null;
      return {
        employee_id: normalizeText(row.employee_id),
        function_id: functionId,
        permission_id: permissionId,
      };
    },
    hasMeaningfulValue(row) {
      return Boolean(normalizeText(row.employee_id));
    },
    getUniqueKey(row) {
      return `${normalizeText(row.employee_id).toLocaleLowerCase('ja')}::${normalizeText(row.function_name).toLocaleLowerCase('ja')}`;
    },
    validateRow(row) {
      const errors = [];
      if (!normalizeText(row.employee_id)) errors.push('社員番号は必須です。');
      if (!normalizeText(row.function_name)) errors.push('機能は必須です。');
      if (!normalizeText(row.permission_name)) errors.push('権限は必須です。');
      return errors;
    },
  },
  buildSimpleUserDefinition({ key: 'functions', label: '機能', valueField: 'function' }),
  buildSimpleUserDefinition({ key: 'permissions', label: '権限', valueField: 'permission' }),
];

export const MASTER_DEFINITIONS = [
  buildSimpleMasterDefinition({
    key: 'oems',
    label: 'OEM',
    valueField: 'oem',
    sampleValue: 'トヨタ',
  }),
  buildSimpleMasterDefinition({
    key: 'regions',
    label: '対地',
    valueField: 'region',
    sampleValue: '日本',
  }),
  buildSimpleMasterDefinition({
    key: 'categories',
    label: 'カテゴリ',
    valueField: 'category',
    sampleValue: '設計',
  }),
  buildSimpleMasterDefinition({
    key: 'large_categories',
    label: '大項目',
    valueField: 'large_category',
    sampleValue: '開発',
  }),
  {
    key: 'middle_categories',
    label: '中項目',
    endpoint: 'middle_categories',
    description: '大項目との親子関係を考慮して中項目を管理します。',
    fields: [
      {
        key: 'large_category_name',
        label: '大項目',
        type: 'select',
        required: true,
        width: '240px',
        getOptions: ({ supportingData }) => [
          '',
          ...supportingData.largeCategories.map((item) => item.large_category),
        ],
      },
      {
        key: 'middle_category',
        label: '中項目',
        type: 'text',
        required: true,
        width: '280px',
      },
    ],
    templateHeaders: ['large_category_name', 'middle_category'],
    templateExample: {
      large_category_name: '開発',
      middle_category: '実装',
    },
    fromApi(item) {
      return {
        id: item.id,
        large_category_name: item.large_category?.name || '',
        middle_category: item.middle_category || '',
      };
    },
    toApiPayload(row, context) {
      const largeCategoryId = context.largeCategoryByName.get(
        normalizeText(row.large_category_name).toLocaleLowerCase('ja'),
      );

      return {
        middle_category: normalizeText(row.middle_category),
        large_category_id: largeCategoryId || null,
      };
    },
    hasMeaningfulValue(row) {
      return Boolean(
        normalizeText(row.large_category_name) || normalizeText(row.middle_category),
      );
    },
    getUniqueKey(row) {
      return `${normalizeText(row.large_category_name).toLocaleLowerCase('ja')}::${normalizeText(
        row.middle_category,
      ).toLocaleLowerCase('ja')}`;
    },
    validateRow(row, context) {
      const errors = [];
      const largeCategoryName = normalizeText(row.large_category_name);
      const middleCategory = normalizeText(row.middle_category);

      if (!largeCategoryName) {
        errors.push('大項目は必須です。');
      }
      if (!middleCategory) {
        errors.push('中項目は必須です。');
      }
      if (
        largeCategoryName
        && !context.largeCategoryByName.has(largeCategoryName.toLocaleLowerCase('ja'))
      ) {
        errors.push('指定された大項目が存在しません。');
      }

      return errors;
    },
  },
  {
    key: 'small_categories',
    label: '小項目',
    endpoint: 'small_categories',
    description: '中項目との親子関係を考慮して小項目を管理します。',
    fields: [
      {
        key: 'middle_category_name',
        label: '中項目',
        type: 'select',
        required: true,
        width: '240px',
        getOptions: ({ supportingData }) => [
          '',
          ...new Set(supportingData.middleCategories.map((item) => item.middle_category)),
        ],
      },
      {
        key: 'small_category',
        label: '小項目',
        type: 'text',
        required: true,
        width: '280px',
      },
    ],
    templateHeaders: ['middle_category_name', 'small_category'],
    templateExample: {
      middle_category_name: '実装',
      small_category: 'コーディング',
    },
    fromApi(item) {
      return {
        id: item.id,
        middle_category_name: item.middle_category?.name || '',
        small_category: item.small_category || '',
      };
    },
    toApiPayload(row, context) {
      const middleCategoryId = context.middleCategoryByName.get(
        normalizeText(row.middle_category_name).toLocaleLowerCase('ja'),
      );

      return {
        small_category: normalizeText(row.small_category),
        middle_category_id: middleCategoryId || null,
      };
    },
    hasMeaningfulValue(row) {
      return Boolean(
        normalizeText(row.middle_category_name) || normalizeText(row.small_category),
      );
    },
    getUniqueKey(row) {
      return `${normalizeText(row.middle_category_name).toLocaleLowerCase('ja')}::${normalizeText(
        row.small_category,
      ).toLocaleLowerCase('ja')}`;
    },
    validateRow(row, context) {
      const errors = [];
      const middleCategoryName = normalizeText(row.middle_category_name);
      const smallCategory = normalizeText(row.small_category);

      if (!middleCategoryName) {
        errors.push('中項目は必須です。');
      }
      if (!smallCategory) {
        errors.push('小項目は必須です。');
      }
      if (
        middleCategoryName
        && !context.middleCategoryByName.has(middleCategoryName.toLocaleLowerCase('ja'))
      ) {
        errors.push('指定された中項目が存在しません。');
      }

      return errors;
    },
  },
  {
    key: 'oem_regions',
    label: 'OEM×対地',
    endpoint: 'oem_regions',
    description: 'OEMと対地の組み合わせを管理します。',
    fields: [
      {
        key: 'oem_name',
        label: 'OEM',
        type: 'select',
        required: true,
        width: '200px',
        getOptions: ({ supportingData }) => [
          '',
          ...(supportingData.oems || []).map((item) => item.oem),
        ],
      },
      {
        key: 'region_name',
        label: '対地',
        type: 'select',
        required: true,
        width: '200px',
        getOptions: ({ supportingData }) => [
          '',
          ...(supportingData.regions || []).map((item) => item.region),
        ],
      },
    ],
    fromApi(item) {
      return {
        id: item.id,
        oem_name: item.oem?.name || '',
        region_name: item.region?.name || '',
      };
    },
    toApiPayload(row, context) {
      const oemId = context.oemByName.get(
        normalizeText(row.oem_name).toLocaleLowerCase('ja'),
      );
      const regionId = context.regionByName.get(
        normalizeText(row.region_name).toLocaleLowerCase('ja'),
      );
      return {
        oem_id: oemId || null,
        region_id: regionId || null,
      };
    },
    hasMeaningfulValue(row) {
      return Boolean(normalizeText(row.oem_name) && normalizeText(row.region_name));
    },
    getUniqueKey(row) {
      return `${normalizeText(row.oem_name).toLocaleLowerCase('ja')}::${normalizeText(row.region_name).toLocaleLowerCase('ja')}`;
    },
    validateRow(row) {
      const errors = [];
      if (!normalizeText(row.oem_name)) errors.push('OEMは必須です。');
      if (!normalizeText(row.region_name)) errors.push('対地は必須です。');
      return errors;
    },
  },
  buildSimpleMasterDefinition({
    key: 'departments',
    label: '設備',
    valueField: 'department',
    sampleValue: '開発部',
  }),
  buildSimpleMasterDefinition({
    key: 'actors',
    label: '領域',
    valueField: 'actor',
    sampleValue: 'フロントエンド',
  }),
  buildSimpleMasterDefinition({
    key: 'statuses',
    label: 'ステータス',
    valueField: 'status',
    sampleValue: '進行中',
  }),
  buildSimpleMasterDefinition({
    key: 'probabilities',
    label: '発生確率',
    valueField: 'probability',
    sampleValue: '中',
  }),
  buildSimpleMasterDefinition({
    key: 'policies',
    label: '対応方針',
    valueField: 'policy',
    sampleValue: '回避',
  }),
  buildSimpleMasterDefinition({
    key: 'causes',
    label: '起因',
    valueField: 'cause',
    sampleValue: '仕様変更',
  }),
  buildSimpleMasterDefinition({
    key: 'impacts',
    label: '影響度',
    valueField: 'impact',
    sampleValue: '高',
  }),
  buildSimpleMasterDefinition({
    key: 'phases',
    label: 'フェーズ',
    valueField: 'phase',
    sampleValue: '11',
  }),
  {
    key: 'schedule_templates',
    label: 'スケジュールテンプレート',
    endpoint: 'schedule_templates',
    description: '全OEM×対地共通の初期値セット（カテゴリ・小項目・領域・設備）を管理します。',
    fields: [
      {
        key: 'category_name',
        label: 'カテゴリ',
        type: 'select',
        required: true,
        width: '180px',
        getOptions: ({ supportingData }) => [
          '',
          ...(supportingData.categories || []).map((item) => item.category),
        ],
      },
      {
        key: 'small_category_name',
        label: '小項目',
        type: 'select',
        required: false,
        width: '200px',
        getOptions: ({ supportingData }) => [
          '',
          ...new Set((supportingData.smallCategories || []).map((item) => item.small_category)),
        ],
      },
      {
        key: 'actor_name',
        label: '領域',
        type: 'select',
        required: true,
        width: '180px',
        getOptions: ({ supportingData }) => [
          '',
          ...(supportingData.actors || []).map((item) => item.actor),
        ],
      },
      {
        key: 'department_name',
        label: '設備',
        type: 'select',
        required: false,
        width: '180px',
        getOptions: ({ supportingData }) => [
          '',
          ...(supportingData.departments || []).map((item) => item.department),
        ],
      },
    ],
    templateHeaders: ['category_name', 'small_category_name', 'actor_name', 'department_name'],
    templateExample: {
      category_name: '設計',
      small_category_name: 'コーディング',
      actor_name: 'フロントエンド',
      department_name: '開発部',
    },
    fromApi(item) {
      return {
        id: item.id,
        category_name: item.category?.name || '',
        small_category_name: item.small_category?.name || '',
        actor_name: item.actor?.name || '',
        department_name: item.department?.name || '',
      };
    },
    toApiPayload(row, context) {
      const categoryId = context.categoryByName.get(
        normalizeText(row.category_name).toLocaleLowerCase('ja'),
      );
      const smallCategoryId = row.small_category_name
        ? context.smallCategoryByName.get(
            normalizeText(row.small_category_name).toLocaleLowerCase('ja'),
          )
        : null;
      const actorId = context.actorByName.get(
        normalizeText(row.actor_name).toLocaleLowerCase('ja'),
      );
      const departmentId = row.department_name
        ? context.departmentByName.get(
            normalizeText(row.department_name).toLocaleLowerCase('ja'),
          )
        : null;

      return {
        category_id: categoryId || null,
        small_category_id: smallCategoryId || null,
        actor_id: actorId || null,
        department_id: departmentId || null,
      };
    },
    hasMeaningfulValue(row) {
      return Boolean(
        normalizeText(row.category_name)
        || normalizeText(row.actor_name),
      );
    },
    getUniqueKey(row) {
      return [
        normalizeText(row.category_name),
        normalizeText(row.small_category_name),
        normalizeText(row.actor_name),
      ].map((v) => v.toLocaleLowerCase('ja')).join('::');
    },
    validateRow(row, context) {
      const errors = [];
      const categoryName = normalizeText(row.category_name);
      const actorName = normalizeText(row.actor_name);

      if (!categoryName) {
        errors.push('カテゴリは必須です。');
      }
      if (!actorName) {
        errors.push('領域は必須です。');
      }
      if (categoryName && !context.categoryByName.has(categoryName.toLocaleLowerCase('ja'))) {
        errors.push('指定されたカテゴリが存在しません。');
      }
      const scName = normalizeText(row.small_category_name);
      if (scName && !context.smallCategoryByName.has(scName.toLocaleLowerCase('ja'))) {
        errors.push('指定された小項目が存在しません。');
      }
      if (actorName && !context.actorByName.has(actorName.toLocaleLowerCase('ja'))) {
        errors.push('指定された領域が存在しません。');
      }
      const depName = normalizeText(row.department_name);
      if (depName && !context.departmentByName.has(depName.toLocaleLowerCase('ja'))) {
        errors.push('指定された設備が存在しません。');
      }

      return errors;
    },
  },
];

export const SUGITTER_DEFINITIONS = [
  {
    key: 'sugitter_posts',
    label: '投稿',
    endpoint: 'sugitter_posts',
    description: 'Sugitter 自由帳の投稿を管理します。',
    fields: [
      { key: 'content', label: '内容', type: 'text', required: true, width: '400px' },
      { key: 'created_at_display', label: '投稿日時', type: 'text', width: '180px', displayOnly: true },
    ],
    fromApi(item) {
      return {
        id: item.id,
        content: item.content || '',
        created_at_display: item.created_at ? new Date(item.created_at).toLocaleString('ja-JP') : '',
      };
    },
    toApiPayload(row) {
      return { content: normalizeText(row.content) };
    },
    hasMeaningfulValue(row) {
      return Boolean(normalizeText(row.content));
    },
    getUniqueKey(row) {
      return String(row.id || normalizeText(row.content));
    },
    validateRow(row) {
      const errors = [];
      if (!normalizeText(row.content)) errors.push('内容は必須です。');
      return errors;
    },
  },
];

export const SUGITTER_GROUPS = [
  { label: '投稿', colorClass: 'schedule', keys: ['sugitter_posts'] },
];

export const LIFE_DEFINITIONS = [
  {
    key: 'life_master_seats',
    label: '座席マスタ',
    endpoint: 'life_master_seats',
    description: 'Life機能の座席マスタを管理します。',
    fields: [
      { key: 'seat_number', label: '座席番号', type: 'text', required: true, width: '180px' },
      { key: 'is_available', label: '利用可能', type: 'checkbox', width: '120px' },
      { key: 'memo', label: '備考', type: 'text', width: '320px' },
    ],
    fromApi(item) {
      return {
        id: item.id,
        seat_number: item.seat_number || '',
        is_available: Boolean(item.is_available),
        memo: item.memo || '',
      };
    },
    toApiPayload(row) {
      return {
        seat_number: normalizeText(row.seat_number),
        is_available: Boolean(row.is_available),
        memo: normalizeText(row.memo) || null,
      };
    },
    hasMeaningfulValue(row) {
      return Boolean(normalizeText(row.seat_number));
    },
    getUniqueKey(row) {
      return normalizeText(row.seat_number).toLocaleLowerCase('ja');
    },
    validateRow(row) {
      const errors = [];
      if (!normalizeText(row.seat_number)) errors.push('座席番号は必須です。');
      return errors;
    },
  },
];

export const LIFE_GROUPS = [
  { label: '基本マスタ', colorClass: 'basic', keys: ['life_master_seats'] },
];

export const MASTER_GROUPS = [
  { label: 'スケジュール', colorClass: 'schedule', keys: ['schedule_templates'] },
  { label: 'カテゴリ', colorClass: 'category', keys: ['categories', 'large_categories', 'middle_categories', 'small_categories'] },
  { label: '基本マスタ', colorClass: 'basic', keys: ['oems', 'regions', 'oem_regions', 'departments', 'actors'] },
  { label: 'トランザクション属性', colorClass: 'attribute', keys: ['statuses', 'probabilities', 'policies', 'causes', 'impacts', 'phases'] },
  { label: 'ユーザー・権限', colorClass: 'user', keys: ['users', 'permission_managements', 'functions', 'permissions'] },
];

export function getMasterDefinition(masterKey) {
  return MASTER_DEFINITIONS.find((d) => d.key === masterKey)
    || USER_DEFINITIONS.find((d) => d.key === masterKey)
    || SUGITTER_DEFINITIONS.find((d) => d.key === masterKey)
    || LIFE_DEFINITIONS.find((d) => d.key === masterKey)
    || null;
}

export function buildMasterTabs() {
  return MASTER_DEFINITIONS.map((definition) => ({
    label: definition.label,
    href: `#/dashboard/master/${definition.key}`,
  }));
}

/**
 * テンプレートヘッダー(APIフィールド名) → 日本語ラベルのマッピング
 */
const HEADER_LABELS = {
  oem: 'OEM名',
  region: '対地名',
  category: 'カテゴリ名',
  large_category: '大項目名',
  large_category_name: '親の大項目名',
  middle_category: '中項目名',
  middle_category_name: '親の中項目名',
  small_category: '小項目名',
  department: '設備名',
  actor: '領域名',
  status: 'ステータス名',
  probability: '発生確率',
  policy: '対応方針名',
  cause: '起因名',
  impact: '影響度',
  phase: 'フェーズ名',
  category_name: 'カテゴリ名',
  small_category_name: '小項目名',
  actor_name: '領域名',
  department_name: '設備名',
};

function getHeaderLabel(fieldName) {
  /* istanbul ignore next -- templateHeaders は全て HEADER_LABELS に登録済み */
  return HEADER_LABELS[fieldName] || fieldName;
}

/**
 * Excelファイルを読み込み、シート名からマスタ種別を判定して
 * { definitionKey, rows[] } の配列を返す
 */
export function parseAllMasterExcel(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      import('xlsx').then((XLSX) => {
        try {
          const wb = XLSX.read(e.target.result, { type: 'array' });
          const results = [];

          for (const sheetName of wb.SheetNames) {
            const def = MASTER_DEFINITIONS.find((d) => d.label === sheetName);
            if (!def) continue;

            const ws = wb.Sheets[sheetName];
            const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1 });
            if (rawRows.length < 2) continue;

            // ヘッダー行: 日本語ラベル → APIフィールド名に変換
            const fileHeaders = rawRows[0].map((h) => String(h).trim());
            const labelToField = new Map(
              def.templateHeaders.map((field) => [getHeaderLabel(field), field]),
            );
            const fieldIndices = def.templateHeaders.map((field) => {
              const label = getHeaderLabel(field);
              let idx = fileHeaders.indexOf(label);
              if (idx === -1) idx = fileHeaders.indexOf(field);
              return idx;
            });

            const hasMissing = fieldIndices.some((idx) => idx === -1);
            if (hasMissing) continue;

            const dataRows = rawRows.slice(1)
              .map((row) => {
                const obj = {};
                def.templateHeaders.forEach((field, i) => {
                  obj[field] = String(row[fieldIndices[i]] ?? '').trim();
                });
                return obj;
              })
              .filter((obj) => Object.values(obj).some((v) => v !== ''));

            if (dataRows.length > 0) {
              results.push({ key: def.key, label: def.label, rows: dataRows });
            }
          }

          resolve(results);
        } catch (err) {
          reject(new Error('Excelファイルの読み込みに失敗しました。'));
        }
      });
    };
    reader.onerror = () => reject(new Error('ファイルの読み込みに失敗しました。'));
    reader.readAsArrayBuffer(file);
  });
}

export function buildMasterTemplateText(definition, delimiter = ',') {
  const headerRow = definition.templateHeaders.join(delimiter);
  const sampleRow = definition.templateHeaders
    .map((header) => definition.templateExample?.[header] || '')
    .join(delimiter);
  return `${headerRow}\n${sampleRow}\n`;
}
